<template>
  <b-modal
    id="associatedImagesModal"
    ref="associatedImagesModal"
    hide-footer
    modal-class="associatedImagesModal"
    static
    @hidden="onHidden"
  >
    <template #modal-title>
      <div>
        {{ $t('global.description.caseAttachments') }}
      </div>
    </template>
    <b-row
      class="attach-body pl-3"
      style="height:100%;"
    >
      <b-col
        cols="3"
        lg="2"
        class="tree p-0 right-bordered"
      >
        <b-row class="py-2 ml-0 tree-content">
          <b-col class="px-1">
            <case-tree
              ref="caseTree"
              :separator="false"
              :case-id="caseId"
              :laboratory-id="laboratoryId"
              :case-study="caseStudy"
              @child-selected="childSelected($event)"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col
        v-if="!isEmpty(selectedTreeElement)"
        class="content pl-0"
      >
        <b-container>
          <b-row>
            <b-col class="attach-header bottom-bordered py-2 d-flex justify-content-between pr-2">
              <div class="d-flex align-items-center">
                <div
                  class="text-center"
                  style="width: 25px"
                >
                  <i
                    :class="`far ${selectedTreeElement.elementIcon} mr-2 main`"
                    aria-hidden="true"
                  />
                </div>
                <span
                  qa="associated-images-modal-entity-number"
                  class="ap-title ap-title__section mx-2"
                >{{ selectedTreeElement.entityDisplay }}</span>
                <vtBubble
                  v-if="selectedTreeElement.attachments.length"
                  :number="selectedTreeElement.attachments.length"
                  :type="false"
                  :tooltip="' '+$tc('global.file',2)"
                />
              </div>
              <div
                v-if="canAttachDocumentsToSelectedEntity"
                class="d-flex align-items-center"
              >
                <b-button
                  v-tooltip="$t('component.attachModal.deleteAttach')"
                  :disabled="!canBeDeleted"
                  class="btn__icon btn__icon--primary pt-1 pr-2"
                  qa="associated-images-modal-delete-button"
                  @click="deleteAttachmentConfirmation"
                >
                  <i
                    class="far fa-trash-alt"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-tooltip="$t('global.description.modifyFileName')"
                  :disabled="!canBeEdited"
                  class="btn__icon btn__icon--primary pt-1 pr-2"
                  qa="associated-images-modal-update-button"
                  @click="showUpdateFileNameModal()"
                >
                  <i
                    class="far fa-edit"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-tooltip="$t('component.attachModal.addAttach')"
                  :disabled="!canBeAttached"
                  class="btn__icon btn__icon--primary pt-1 pr-4"
                  qa="associated-images-modal-upload-button"
                  @click="showAddAttachmentModal"
                >
                  <i
                    class="far fa-paperclip"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-container>
        <div
          class="thumb-list-pictures thumb-list px-3"
          qa="associated-images-modal-container"
        >
          <b-row class="py-3 pr-3">
            <vt-thumbnail
              v-for="(attachment) in selectedTreeElement.attachments"
              id="attachmentThumbnail"
              :key="attachment.attachmentId"
              ref="attachmentThumbnail"
              :laboratory-id="laboratoryId"
              :case-id="selectedTreeElement.caseId"
              :sample-id="selectedTreeElement.sampleId"
              :portion-id="selectedTreeElement.portionId"
              :subportion-id="selectedTreeElement.subportionId"
              :attachment="attachment"
              @attachmentChecked="getAttachmentsChecked($event)"
              @attachmentSelected="openViewer($event)"
            />
          </b-row>
        </div>
        <div class="thumb-footer text-right p-3">
          <b-button
            class="btn--primary"
            qa="associated-images-modal-add-button"
            size="sm"
            @click="add"
          >
            {{ $t('global.add') }} ({{ attachmentsCheckedToAssociate.length }})
          </b-button>
        </div>
      </b-col>
    </b-row>

    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            size="sm"
            class="btn--primary"
            @click="hide"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />
            {{ $t('global.accept') }}
          </b-button>
        </div>
      </div>
    </div>
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="selectedTreeElement.attachmentEntityType"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <modal-confirmation
      id="associatedImageConfirmationModal"
      ref="confirmationModal"
    />
    <modal-wrapper ref="updateAttachmentNameModal">
      <update-attachment-name-modal
        :attachment-entity-type="selectedTreeElement.attachmentEntityType"
        :entities-id-array="entitiesIdArray"
        :attachment-id="attachmentId"
        :file-name="fileName"
        @attachmentNameUpdated="onAttachmentNameUpdated"
      />
    </modal-wrapper>
  </b-modal>
</template>

<script>
import VtThumbnail from '@/components/common/VtThumbnail'
import VtBubble from '@/components/common/VtBubble'
import CaseTree from '@/components/attachments/CaseTree'
import AddFileModal from '@/components/AddFileModal'
import patientAttachmentService from '@/api/resources/attachment/patientAttachmentService'
import requestAttachmentService from '@/api/resources/attachment/requestAttachmentService'
import caseAttachmentService from '@/api/resources/attachment/caseAttachmentService'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import portionAttachmentService from '@/api/resources/attachment/portionAttachmentService'
import subportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import apiExceptionType from '@/utils/apiExceptionType'
import UpdateAttachmentNameModal from '@/components/attachments/UpdateAttachmentNameModal'
import caseService from '@/api/resources/common/commonCaseServices'
import attachmentsMixin from '@/components/attachments/mixins/attachmentsMixin'
import { isEmpty } from 'lodash'
import { ViewerType } from '@/modules/viewerModule/ViewerModule.models'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ATTACHMENT_MIME_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import { SECTION_COMPLEMENTARIES } from '@/modules/diagnosisModule/constants/diagnosis-sections'
import complementaryAttachmentService from '@/api/resources/attachment/complementaryAttachmentService'

export default {
    name: 'AssociatedImagesModal',
    components: {
        VtThumbnail,
        VtBubble,
        CaseTree,
        ModalConfirmation,
        AddFileModal,
        UpdateAttachmentNameModal
    },
    mixins: [
        attachmentsMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-list-ul' }) },
        associatedImages: { type: Array },
        id: { type: String },
        content: { type: String },
        okTitle: { type: String, default: 'global.close' },
        laboratoryId: { type: Number },
        caseId: { type: Number },
        complementaryId: { type: Number },
        diagnosisSection: { type: String }
    },
    data() {
        return {
            nodeCode: null,
            selectedTreeElement: {},
            caseStudy: {},
            attachmentsChecked: [],
            attachments: [],
            mode: this.initMode,
            entitiesIdArray: [],
            fileName: '',
            attachmentId: null
        }
    },
    computed: {
        canBeAttached() {
            return this.selectedTreeElement.canBeAttached
        },
        canBeDeleted() {
            return this.selectedTreeElement.canBeAttached && this.attachmentsChecked.length
        },
        canBeEdited() {
            return this.selectedTreeElement.canBeAttached && (this.attachmentsChecked.length === 1)
        },
        attachmentsCheckedToAssociate() {
            return this.attachmentsChecked.filter(attachment => !attachment.associated && this.isImageAttachment(attachment))
        },
        canAttachDocumentsToSelectedEntity() {
            return this.canAttachDocumentsToEntityType(this.selectedTreeElement)
        }
    },
    methods: {
        add() {
            let data = {
                attachmentEntityType: this.selectedTreeElement.attachmentEntityType,
                attachments: this.attachmentsChecked.filter(attachment => !attachment.associated)
            }
            if (this.nodeCode) {
                data.nodeCode = this.nodeCode
            }
            this.$emit('associateImages', data)
            this.hide()
        },
        async onAttachmentNameUpdated() {
            await this.show(this.selectedTreeElement)
            this.$emit('attachmentNameUpdated')
        },
        async onAttachmentsAdded() {
            await this.show(this.selectedTreeElement)
            this.$emit('attachments-updated')
        },
        async getHierarchicalCaseIndexTree() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(caseService.hierarchicalCaseIndexTree(this.laboratoryId, this.caseId, true))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            return response
        },
        isImageAttachment(thumbnail) {
            return thumbnail.attachment.mimetype.startsWith(ATTACHMENT_MIME_TYPES.IMAGE)
        },
        setAssociatedImages() {
            this.associatedImages.forEach(associatedImage => {
                const associatedAttachment = this.selectedTreeElement.attachments.find(elementAttachment =>
                    elementAttachment.attachmentId === associatedImage.attachment.attachmentId && this.selectedTreeElement.attachmentEntityType === associatedImage.attachmentEntityType)
                if (associatedAttachment) {
                    associatedAttachment.associated = true
                }
            })
        },
        childSelected(child) {
            if (child !== this.selectedTreeElement) {
                if (!isEmpty(this.selectedTreeElement)) {
                    this.selectedTreeElement.deselect()
                    this.selectedTreeElement = child
                }
            }
            this.getAttachmentsChecked()
            this.setAssociatedImages()
        },
        findCaseTree() {
            if (!this.$refs.caseTree) {
                return null
            }
            return this.$refs.caseTree
        },
        async show(selectedTreeElement, nodeCode, sampleId) {
            if (!this.nodeCode) {
                this.nodeCode = nodeCode
            }
            this.attachmentsChecked = []
            this.caseStudy = await this.getHierarchicalCaseIndexTree()
            if (this.caseStudy) {
                this.$refs.associatedImagesModal.show()
                if (this.diagnosisSection === SECTION_COMPLEMENTARIES) {
                    this.showComplementary(this.complementaryId)
                } else {
                    if (!sampleId) {
                        this.selectedTreeElement = isEmpty(selectedTreeElement) ? this.$refs.caseTree : selectedTreeElement
                        this.selectedTreeElement.elementSelected()
                    } else {
                        this.showSample(sampleId)
                    }
                }
            }
        },
        showSample(sampleId) {
            this.$nextTick(() => {
                const selectedSampleTreeElement = this.$refs.caseTree.findSampleTree(sampleId)
                if (selectedSampleTreeElement) {
                    this.selectedTreeElement = selectedSampleTreeElement
                    this.selectedTreeElement.elementSelected()
                }
            })
        },
        showComplementary(complementaryId) {
            this.$nextTick(() => {
                const foundChild = this.findComplementaryTree(complementaryId)
                if (foundChild) {
                    this.selectedTreeElement = foundChild
                    this.selectedTreeElement.elementSelected()
                }
            })
        },
        findComplementaryTree(complementaryId) {
            if (!this.$refs.caseTree) {
                return null
            }
            const complementaryTree = this.$refs.caseTree.findComplementaryTree(complementaryId)
            if (complementaryTree) {
                return complementaryTree
            }
        },
        getAttachmentsChecked(attachmentChecked) {
            this.$nextTick(() => {
                this.attachmentsChecked = this.$refs.attachmentThumbnail ? this.$refs.attachmentThumbnail.filter(thumbnail => thumbnail.isSelected) : []
            })
            if (this.nodeCode && attachmentChecked) {
                this.attachmentsChecked.forEach(attachmentThumbnail => {
                    if (attachmentThumbnail.attachment.attachmentId !== attachmentChecked.attachmentId) {
                        attachmentThumbnail.deselect()
                    }
                })
            }
        },
        showUpdateFileNameModal() {
            this.entitiesIdArray = this.setEntitiesIdArray(this.selectedTreeElement, this.laboratoryId)
            this.fileName = this.attachmentsChecked[0].attachment.filename
            this.attachmentId = this.attachmentsChecked[0].attachment.attachmentId
            this.$refs.updateAttachmentNameModal.show()
        },
        onHidden() {
            this.selectedTreeElement.deselect()
        },
        hide() {
            this.$refs.associatedImagesModal.hide()
        },
        showAddAttachmentModal() {
            this.entitiesIdArray = this.setEntitiesIdArray(this.selectedTreeElement, this.laboratoryId)
            this.$refs.addFileModalWrapper.show()
        },
        openViewer(query) {
            if (query.atyDescription === 'attachment.type.macroscopicimage.description' ||
                query.atyDescription === 'attachment.type.microscopicimage.description' ||
                (query.imageType && query.imageType !== 'OTHER')) {
                viewerService.openViewer({
                    router: this.$router,
                    viewerType: ViewerType.STATIC_VIEWER,
                    laboratoryId: query.laboratoryId,
                    caseId: query.caseId,
                    sampleId: query.sampleId,
                    attachmentId: query.attachmentId,
                    label: query.label,
                    portionId: query.portionId,
                    subportionId: this.selectedTreeElement.subportion ? this.selectedTreeElement.subportion.supId : undefined
                })
            } else {
                window.open(query.url, '_blank')
            }
        },
        async addAttachment(file, errorMessage, addAttachmentFunction) {
            this.updateLoading(true)
            const [err] = await this.toCall(addAttachmentFunction(file))
            if (err) {
                if (err.response.data.code === apiExceptionType.MAX_UPLOAD_SIZE_EXCEEDED || err.response.status === apiExceptionType.ENTITY_TOO_LARGE) {
                    this.showErrorModalGlobal(this.$t('component.attachModal.error.maxFileSizeExceeded'))
                } else {
                    this.showErrorModalGlobal(this.$t(errorMessage))
                }
            } else {
                this.showToastSuccessGlobal()
                this.attachmentsChecked = []
                this.caseStudy = await this.getHierarchicalCaseIndexTree()
            }
            this.updateLoading(false)
        },
        deleteAttachmentConfirmation() {
            this.$refs.confirmationModal.ask(this.deleteAttachments)
        },
        async deleteAttachments() {
            this.updateLoading(true)
            const selectedIds = []
            this.$refs.attachmentThumbnail.forEach((thumbnail) => {
                if (thumbnail.isSelected) {
                    selectedIds.push(thumbnail.attachment.attachmentId)
                }
            })

            let deleteFunction = () => {
            }
            let errorMessage = ''

            if (this.selectedTreeElement.subportionId) {
                deleteFunction = (attachmentId) => {
                    return subportionAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, this.selectedTreeElement.portionId, this.selectedTreeElement.subportionId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.subportionAttachmentDeleteError'
            } else if (this.selectedTreeElement.portionId) {
                deleteFunction = (attachmentId) => {
                    return portionAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, this.selectedTreeElement.portionId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.portionAttachmentDeleteError'
            } else if (this.selectedTreeElement.complementaryId) {
                deleteFunction = (attachmentId) => {
                    return complementaryAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.complementaryId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.complementaryAttachmentError'
            } else if (this.selectedTreeElement.sampleId) {
                deleteFunction = (attachmentId) => {
                    return sampleAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.sampleAttachmentDeleteError'
            } else if (this.selectedTreeElement.caseId) {
                deleteFunction = (attachmentId) => {
                    return caseAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.caseAttachmentDeleteError'
            } else if (this.selectedTreeElement.requestId) {
                deleteFunction = (attachmentId) => {
                    return requestAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.requestId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.requestAttachmentDeleteError'
            } else if (this.selectedTreeElement.patientId) {
                deleteFunction = (attachmentId) => {
                    return patientAttachmentService.deleteAttachment(this.selectedTreeElement.patientId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.patientAttachmentDeleteError'
            }

            let errors = false
            for (let attachmentId of selectedIds) {
                const [err] = await this.toCall(deleteFunction(attachmentId))
                if (err) {
                    errors = true
                }
            }
            if (errors) {
                this.showErrorModalGlobal(this.$t(errorMessage))
            }
            this.caseStudy = await this.getHierarchicalCaseIndexTree()
            await this.selectedTreeElement.elementSelected()
            this.$emit('attachments-updated')
            this.attachmentsChecked = []
            this.updateLoading(false)
        },
        isEmpty(value) {
            return isEmpty(value)
        }
    }
}

</script>

<style lang="scss">

   .associatedImagesModal .thumb-card.associated {
      border-color: vt-color(green-radiant);
   }

  .associatedImagesModal .modal-dialog {
    max-width: 95%;
  }

  .associatedImagesModal .attach-body {
    padding: 0;
  }

  .associatedImagesModal .thumb-list{
    height: calc(100vh - 190px);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .associatedImagesModal .thumb-list.thumb-list-pictures{
      height: calc(100vh - 240px);
  }

  .associatedImagesModal .modal-body {
    padding: 0;
    border-radius: .3rem;
  }

  .associatedImagesModal .patientCard .flex-btn {
    width: 4% !important;
  }

  .associatedImagesModal .patientCard .title-container {
    margin-right: 2rem;
  }

  .associatedImagesModal .patientCard i {
    font-size: 1.9rem !important;
  }

  .associatedImagesModal .content .container {
    max-width: 100%;
  }

  .associatedImagesModal .tree {
    border-right: 1px solid #dee2e6;
    background: vt-color(base-text,.90);
  }

  .associatedImagesModal .attach-header {
    border-bottom: 1px solid #dee2e6;
  }

  .associatedImagesModal .attach-content {
    height: calc(100vh - 235px);
    overflow: hidden;
    overflow-y: auto;
    margin-right: 5px;
  }

  .associatedImagesModal .tree-content {
    height: calc(100vh - 155px);
    overflow: hidden;
    overflow-y: auto;
    margin-right: 0;
    display: flex;
    flex-direction: column;
  }

  .associatedImagesModal .tree .element .btn {
    background: vt-color(white-light);
    color: vt-color(dark-text);
    border-color: #dee2e6;
    border-radius: 2px;
    width: 100%;
    margin-bottom: 5px;
    box-shadow: 0 1px 1px 0 vt-color(base-text, .7);
    font-size: font-size(very-small);
  }

  .associatedImagesModal .tree .element .btn i {
    transition: .3s;
  }

  .associatedImagesModal .tree .element .btn .attach-arrow i:hover {
    color: vt-color(primary);
  }

  .associatedImagesModal .tree .element .btn .attach-arrow i {
    color: vt-color(base-text);
    font-size: font-size(tiny);
  }

  .associatedImagesModal .tree .element .arrow {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: .9rem;
  }

  .associatedImagesModal .tree .element .btn:hover {
    background: vt-color(light-primary);
    border-color: vt-color(alt-primary, .5);
  }

  .attach-arrow {
    padding: 0 3px;
  }

  .attach-display {
    display: none;
  }

  .attach-pressed {
    background: vt-color(primary) !important;
    border-radius: 3px !important;
    color: #FFF !important;
  }

  .attach-pressed i {
    color: #FFF !important;
  }

  .case-separator{
    border-top: 2px solid vt-color(base-text, .7);
  }

  .associatedImagesModal .sidepanel {
    border-left: 1px solid #dee2e6;
    background: #FFF;
    width: 300px;
    position: absolute;
    right: -300px;
    height: 100%;
    transition: .3s;
    box-shadow: 0 3px 5px 0 #9c9c9c;
  }

  .associatedImagesModal .sidepanel.animation {
    right: 0;
  }

  .associatedImagesModal .sidepanel .header {
    border-bottom: 1px solid #dee2e6;
  }

  .associatedImagesModal .sidepanel .title {
    color: vt-color(primary);
  }

  .associatedImagesModal .sidepanel .content {
    height: 100%;
    overflow: hidden;
    overflow-y: scroll;
  }

  .associatedImagesModal .sidepanel .file {
    border-bottom: 1px solid #dee2e6;
  }

  .associatedImagesModal .sidepanel .file .others {
    color: grey;
    font-size: .9rem;
  }

  .associatedImagesModal .sidepanel .element .col-md-4 {
    max-width: 100% !important;
    padding-left: 10px;
    margin-top: 5px;
    margin-bottom: 0 !important;
  }

  .associatedImagesModal .tree .element .btn.attach-pressed {
    border: 1px solid vt-color(primary);
  }

</style>
